import { EmptySpaceFiller, GroupLabel } from './SharedGroupRuleElements';
import {
  FieldGroupingRule,
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsFields,
  PerspectivesGroupsWorkspace,
} from '../types';
import { Select } from '@ardoq/select';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { getWorkspaceOptions } from './getWorkspaceOptions';
import { DisabledOptionProps } from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

type FieldGroupRuleProps = {
  groupingRule: FieldGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  fieldsPerWorkspace: Record<ArdoqId, PerspectivesGroupsFields[]>;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  tabIndex?: number;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  i18nLocale: I18nLocale;
};

const toFieldOption = (field: PerspectivesGroupsFields) => ({
  value: field.id,
  label: field.name,
});

const FieldGroupRule = ({
  workspaces,
  groupingRule,
  fieldsPerWorkspace,
  onGroupingRuleUpdated,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale,
}: FieldGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = getWorkspaceOptions({
    workspaces,
    disabledOptionProps,
    entitiesName: 'fields',
    firstSelectOption: workspaceSelectOption,
    entitiesPerWorkspace: fieldsPerWorkspace,
  });

  const fields = fieldsPerWorkspace[groupingRule.workspaceId!] ?? [];
  const fieldOptions = [
    ruleTargetSelectOption,
    ...fields.map(toFieldOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <GroupLabel data-testid={`group-by-${GroupType.FIELD}-label`}>
        {t(i18nLocale, 'perspectives:groupingRules.fieldLabel', 'Field')}
      </GroupLabel>
      <Select
        value={groupingRule.workspaceId}
        dataTestId="field-grouping-rule-workspace-select"
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultFieldId = fieldsPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultFieldId,
          });
        }}
      />
      <Select
        options={fieldOptions}
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.fieldsAriaLabel',
          'Fields'
        )}
        dataTestId="field-grouping-rule-field-select"
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
      />

      <EmptySpaceFiller />
      <EmptySpaceFiller />
    </>
  );
};

export { FieldGroupRule };
